// src/components/userdashboard.jsx
// ── Updated: Edit Profile tab, first name welcome, mobile refinements ──

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getAuth, signOut, updateProfile, onAuthStateChanged } from 'firebase/auth';
import {
  doc, onSnapshot, updateDoc, serverTimestamp,
  collection, query, where, orderBy, limit, increment, writeBatch,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth } from '../firebase';
import Referral from './referral';
import SupportTicket from './SupportTicket';
import SubscriptionPage from './SubscriptionPage';
import StyleTryon from './StyleTryon';
import { useSubscription } from '../hooks/useSubscription';
import toast from 'react-hot-toast';

const NAVY        = '#001F3F';
const GOLD        = '#B8860B';
const DARK_NAVY   = '#050a0f';
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];
const SIDEBAR_W   = 260;

// ── NAV LINKS ─────────────────────────────────────────────────────────────────
// 'profile' is handled as a special active view, not a route
const NAV_LINKS = [
  { name: 'Dashboard',       path: '/dashboard',        icon: '🏠' },
  { name: '3D Scan',         path: '/scan',             icon: '👤' },
  { name: 'Biometric Health',path: '/biometric-health', icon: '🧬' },
  { name: 'Style Try-On',    path: '#try-on',           icon: '🪄', special: 'tryon'   },
  { name: '3D Viewer',       path: '/viewer',           icon: '👓' },
  { name: 'Wallet',          path: '/wallet',           icon: '💰' },
  { name: 'Global Vault',    path: '/gallery',          icon: '🌍' },
  { name: 'Edit Profile',    path: '#profile',          icon: '⚙️', special: 'profile' },
];

export default function UserDashboard() {
  const [userData,        setUserData]        = useState(null);
  const [activeBookings,  setActiveBookings]  = useState([]);
  const [notifications,   setNotifications]   = useState([]);
  const [healthAlerts,    setHealthAlerts]    = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [showSupport,     setShowSupport]     = useState(false);
  const [showSubModal,    setShowSubModal]    = useState(false);
  const [showStyleTryOn,  setShowStyleTryOn]  = useState(false);
  const [showProfile,     setShowProfile]     = useState(false);
  const [unreadCount,     setUnreadCount]     = useState(0);
  const [isMobile,        setIsMobile]        = useState(window.innerWidth < 1024);
  const [sidebarOpen,     setSidebarOpen]     = useState(window.innerWidth >= 1024);

  // Edit profile state
  const [profileForm,     setProfileForm]     = useState({ displayName: '', phone: '', email: '' });
  const [savingProfile,   setSavingProfile]   = useState(false);

  const sub          = useSubscription();
  const user         = auth.currentUser;
  const isSuperAdmin = user && SUPER_ADMINS.includes(user.uid);
  const firstName    = user?.displayName?.split(' ')[0] || 'Member';
  const fileInputRef = useRef(null);
  const navigate     = useNavigate();
  const location     = useLocation();
  const storage      = getStorage();

  const toastStyle = {
    style: { background: NAVY, color: GOLD, border: `1px solid ${GOLD}`, borderRadius: '12px', fontFamily: 'Montserrat, sans-serif' },
  };

  // ── Responsive ───────────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Data listeners ────────────────────────────────────────────────────────
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) { navigate('/login'); return; }

      // Seed profile form with current auth data
      setProfileForm({
        displayName: currentUser.displayName || '',
        phone:       '',
        email:       currentUser.email || '',
      });

      const unsubUser = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setUserData(data);
          // Merge saved phone into form
          setProfileForm(prev => ({
            ...prev,
            displayName: data.displayName || currentUser.displayName || '',
            phone:       data.phone || '',
          }));
          if (data.healthAlerts) {
            setHealthAlerts(data.healthAlerts.filter(a => a.type !== 'reward' && a.type !== 'credit').slice().reverse());
          }
        }
        setLoading(false);
      });

      const qNotifs = query(collection(db, 'users', currentUser.uid, 'notifications'), orderBy('createdAt', 'desc'), limit(15));
      const unsubNotifs = onSnapshot(qNotifs, (snap) => {
        const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.read).length);
      }, err => console.error('Notification error:', err));

      const qBookings = query(collection(db, 'bookings'), where('clientId', '==', currentUser.uid), where('escrowStatus', '==', 'held'));
      const unsubBookings = onSnapshot(qBookings, (snap) => {
        setActiveBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });

      return () => { unsubUser(); unsubNotifs(); unsubBookings(); };
    });
    return () => unsubAuth();
  }, [navigate]);

  // ── Identity swap ─────────────────────────────────────────────────────────
  const handleIdentitySwap = async () => {
    if (!isSuperAdmin) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), { role: 'stylist' });
      toast.success('SWAPPING TO STYLIST PRO HUB...', toastStyle);
      setTimeout(() => { navigate('/stylist-dashboard'); window.location.reload(); }, 1000);
    } catch { toast.error('Swap failed', toastStyle); }
  };

  // ── Profile photo upload ──────────────────────────────────────────────────
  const handleProfileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const toastId = toast.loading('Updating profile photo...', toastStyle);
    try {
      const storRef = ref(storage, `profiles/${auth.currentUser.uid}`);
      await uploadBytes(storRef, file);
      const url = await getDownloadURL(storRef);
      await updateProfile(auth.currentUser, { photoURL: url });
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { photoURL: url });
      toast.success('Photo updated!', { id: toastId, ...toastStyle });
    } catch { toast.error('Upload failed.', { id: toastId, ...toastStyle }); }
  };

  // ── Save profile ──────────────────────────────────────────────────────────
  const saveProfile = async () => {
    if (!user) return;
    try {
      setSavingProfile(true);
      await updateProfile(user, { displayName: profileForm.displayName });
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: profileForm.displayName,
        phone:       profileForm.phone,
        updatedAt:   serverTimestamp(),
      });
      toast.success('Profile updated!', toastStyle);
      setShowProfile(false);
    } catch (err) {
      console.error(err); toast.error('Update failed', toastStyle);
    } finally { setSavingProfile(false); }
  };

  // ── Release escrow ────────────────────────────────────────────────────────
  const releaseEscrow = async (booking) => {
    if (!window.confirm(`Release payment? 90% goes to ${booking.stylistName}'s wallet.`)) return;
    const toastId = toast.loading('Splitting funds...', toastStyle);
    try {
      const batch = writeBatch(db);
      const stylistEarnings  = booking.totalPrice * 0.90;
      const esdrasCommission = booking.totalPrice * 0.10;
      batch.update(doc(db, 'bookings', booking.id), {
        escrowStatus: 'released', status: 'completed',
        releasedAt: serverTimestamp(), stylistShare: stylistEarnings, platformShare: esdrasCommission,
      });
      batch.update(doc(db, 'users', booking.stylistId), { walletBalance: increment(stylistEarnings) });
      batch.update(doc(db, 'admin', 'finances'), { totalCommissionEarned: increment(esdrasCommission) });
      await batch.commit();
      toast.success('Stylist credited.', { id: toastId, ...toastStyle });
    } catch (err) { console.error(err); toast.error('Transaction failed.', { id: toastId, ...toastStyle }); }
  };

  // ── Nav link click handler ────────────────────────────────────────────────
  const handleNavClick = (link, e) => {
    if (link.special === 'tryon') {
      e.preventDefault();
      if (!sub.canStyleTryOn.allowed && !sub.isSuperAdmin) setShowSubModal(true);
      else setShowStyleTryOn(true);
      if (isMobile) setSidebarOpen(false);
      return;
    }
    if (link.special === 'profile') {
      e.preventDefault();
      setShowProfile(true);
      if (isMobile) setSidebarOpen(false);
      return;
    }
    if (isMobile) setSidebarOpen(false);
  };

  if (loading) return (
    <div style={{ height: '100vh', display: 'grid', placeItems: 'center', background: '#000' }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid rgba(184,134,11,0.2)', borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  const isSubActive = sub.isSubscriptionActive;
  const scanCap     = isSubActive ? (sub.PRICING?.[sub.tier]?.scansPerMonth || 0) : sub.FREE_LIMITS?.scans;
  const scanUsed    = isSubActive ? sub.monthlyScansUsed : sub.lifetimeScansUsed;
  const tryOnUsed   = sub.tryOnCount || sub.lifetimeStylesUsed || 0;
  const tryOnCap    = isSubActive ? '∞' : String(sub.FREE_LIMITS?.styleTryOns || 1);

  const mainPaddingLeft = (!isMobile && sidebarOpen) ? `${SIDEBAR_W}px` : '0';

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: 'white', fontFamily: 'Montserrat, sans-serif' }}>
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input:focus, textarea:focus { outline: none; border-color: ${GOLD} !important; }
        @media (max-width: 1023px) {
          .user-grid { grid-template-columns: 1fr !important; }
          .dash-header-top { flex-direction: column !important; gap: 12px !important; }
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(184,134,11,0.3); border-radius: 4px; }
      `}</style>

      {/* Mobile toggle */}
      {isMobile && (
        <button onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{ position: 'fixed', top: '15px', left: '15px', zIndex: 1100, background: GOLD, border: 'none', borderRadius: '8px', padding: '8px 12px', fontWeight: 'bold', color: 'black', cursor: 'pointer', fontSize: '1rem' }}>
          {sidebarOpen ? '✕' : '☰'}
        </button>
      )}

      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 998 }} />
      )}

      {/* ── SIDEBAR ── */}
      <aside style={{
        position: 'fixed', top: 0, bottom: 0,
        left: sidebarOpen ? '0' : `-${SIDEBAR_W}px`,
        width: `${SIDEBAR_W}px`, background: DARK_NAVY,
        borderRight: '1px solid rgba(255,255,255,0.05)',
        display: 'flex', flexDirection: 'column', zIndex: 999,
        transition: 'left 0.3s ease', overflowY: 'auto',
      }}>
        <div style={{ padding: '30px 20px', textAlign: 'center' }}>
          <button onClick={() => navigate('/')}
            style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: GOLD, padding: '10px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
            🏠 HOME
          </button>
          <h2 style={{ color: GOLD, fontSize: '0.8rem', letterSpacing: '5px', fontWeight: '900', margin: '15px 0' }}>ESDRAS</h2>
          {isSuperAdmin && (
            <div style={{ background: GOLD, color: 'black', padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.6rem', display: 'inline-block' }}>
              ⚡ ROOT ACCESS
            </div>
          )}
        </div>

        {/* Subscription badge */}
        <div style={{ padding: '0 20px 20px' }}>
          <div onClick={() => setShowSubModal(true)}
            style={{ padding: '15px', borderRadius: '16px', background: (isSubActive || sub.isSuperAdmin) ? 'linear-gradient(135deg,#001F3F 0%,#050a0f 100%)' : 'rgba(255,255,255,0.03)', border: `1px solid ${(isSubActive || sub.isSuperAdmin) ? GOLD : 'rgba(255,255,255,0.1)'}`, cursor: 'pointer', textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', marginBottom: '5px' }}>
              {sub.isSuperAdmin ? '⚡' : isSubActive ? '🛡️' : '🔓'}
            </div>
            <p style={{ margin: 0, fontSize: '0.65rem', fontWeight: '900', color: GOLD, letterSpacing: '1px' }}>
              {sub.isSuperAdmin ? 'ROOT ACCESS' : isSubActive ? `SHIELD · ${sub.tier?.toUpperCase()}` : 'UPGRADE TO SHIELD'}
            </p>
            <p style={{ margin: '5px 0 0', fontSize: '0.55rem', opacity: 0.6, color: 'white' }}>
              {sub.isSuperAdmin ? 'Unlimited access' : isSubActive ? `${scanUsed}/${scanCap} scans this month` : 'From ₦3,000/month'}
            </p>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto' }}>
          {NAV_LINKS.map(link => {
            const isActive = location.pathname === link.path ||
              (link.special === 'profile' && showProfile);
            return (
              <Link key={link.name} to={link.path}
                onClick={(e) => handleNavClick(link, e)}
                style={{ display: 'flex', alignItems: 'center', padding: '14px 24px', textDecoration: 'none', fontSize: '0.8rem', color: isActive ? GOLD : '#ffffffb3', background: isActive ? 'rgba(184,134,11,0.12)' : 'transparent', borderLeft: isActive ? `4px solid ${GOLD}` : '4px solid transparent', transition: '0.2s' }}>
                <span style={{ fontSize: '1.1rem', minWidth: '35px' }}>{link.icon}</span>
                {link.name}
              </Link>
            );
          })}
        </nav>

        <div style={{ padding: '20px' }}>
          <button onClick={() => signOut(auth)}
            style={{ width: '100%', padding: '12px', background: 'rgba(255,77,77,0.1)', color: '#ff4d4d', border: '1px solid rgba(255,77,77,0.2)', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontFamily: 'Montserrat, sans-serif' }}>
            LOGOUT
          </button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <main style={{ paddingLeft: mainPaddingLeft, transition: 'padding-left 0.3s ease', minHeight: '100vh', width: '100%' }}>

        {/* Header */}
        <header style={{ background: NAVY, padding: '2.5rem 2rem', borderBottom: `4px solid ${GOLD}` }}>
          <div className="dash-header-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ color: GOLD, fontSize: 'clamp(1.2rem, 3vw, 1.8rem)', letterSpacing: '4px', margin: 0, fontWeight: '900' }}>CLIENT HUB</h1>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', letterSpacing: '2px', margin: '4px 0 0' }}>ESDRAS_USER_NODE_v9.4</p>
            </div>
            {isSuperAdmin && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => navigate('/admin')} style={superSwitch}>SYSTEM PANEL</button>
                <button onClick={() => navigate('/admin/finance-ledger')} style={superSwitch}>LEDGER</button>
                <button onClick={handleIdentitySwap} style={{ background: GOLD, color: 'black', border: 'none', padding: '10px 18px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: '900', cursor: 'pointer' }}>
                  STYLIST VIEW
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => fileInputRef.current.click()}>
              <img src={userData?.photoURL || `https://ui-avatars.com/api/?name=${firstName}&background=001F3F&color=B8860B`}
                style={{ width: '80px', height: '80px', borderRadius: '50%', border: `3px solid ${GOLD}`, objectFit: 'cover' }}
                alt="profile" />
              <div style={{ position: 'absolute', bottom: '2px', right: '2px', background: GOLD, borderRadius: '50%', width: '22px', height: '22px', display: 'grid', placeItems: 'center', fontSize: '0.65rem', color: 'black' }}>📸</div>
              <input type="file" ref={fileInputRef} hidden onChange={handleProfileUpload} accept="image/*" />
            </div>
            <div>
              <h1 style={{ fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', fontWeight: '800', margin: 0, textTransform: 'lowercase' }}>
                welcome, <span style={{ color: GOLD }}>{firstName}</span>
              </h1>
              <div style={{ display: 'flex', gap: '10px', marginTop: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.6rem', color: GOLD, border: `1px solid ${GOLD}55`, padding: '2px 8px', borderRadius: '4px', fontWeight: '900' }}>
                  ESDRAS BIOMETRIC VAULT
                </span>
                {isSubActive && (
                  <span style={{ fontSize: '0.6rem', background: GOLD, color: NAVY, padding: '2px 8px', borderRadius: '4px', fontWeight: '900' }}>
                    {sub.tier?.toUpperCase()} SHIELD
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Content area — switches between main dashboard and edit profile */}
        {showProfile ? (

          /* ── EDIT PROFILE VIEW ──────────────────────────────────────────── */
          <div style={{ maxWidth: '680px', margin: '0 auto', padding: '2rem 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <button onClick={() => setShowProfile(false)}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '8px 14px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'Montserrat, sans-serif' }}>
                ← Back
              </button>
              <h2 style={{ margin: 0, color: GOLD, fontSize: '0.9rem', letterSpacing: '3px', fontWeight: '900' }}>EDIT PROFILE</h2>
            </div>

            <div style={{ background: '#080c12', borderRadius: '24px', padding: '28px', border: '1px solid rgba(184,134,11,0.15)' }}>

              {/* Photo */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
                <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()}>
                  <img src={userData?.photoURL || `https://ui-avatars.com/api/?name=${firstName}&background=001F3F&color=B8860B`}
                    style={{ width: '100px', height: '100px', borderRadius: '50%', border: `3px solid ${GOLD}`, objectFit: 'cover', display: 'block' }}
                    alt="profile" />
                  <div style={{ position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.8)', color: GOLD, fontSize: '0.52rem', fontWeight: '900', padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap', letterSpacing: '1px' }}>
                    📸 CHANGE
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

                {/* Display name */}
                <div>
                  <label style={labelStyle}>Full Name</label>
                  <input style={inputStyle} placeholder="Your full name"
                    value={profileForm.displayName}
                    onChange={e => setProfileForm({ ...profileForm, displayName: e.target.value })} />
                </div>

                {/* Phone */}
                <div>
                  <label style={labelStyle}>Phone Number</label>
                  <input style={inputStyle} type="tel" placeholder="+234 xxx xxx xxxx"
                    value={profileForm.phone}
                    onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} />
                </div>

                {/* Email — display only, can't change Firebase email here */}
                <div>
                  <label style={labelStyle}>Email Address</label>
                  <input style={{ ...inputStyle, opacity: 0.5, cursor: 'not-allowed' }} disabled
                    value={profileForm.email} />
                  <p style={{ margin: '4px 0 0', fontSize: '0.58rem', opacity: 0.4 }}>Email changes require re-authentication. Contact support.</p>
                </div>

                <button onClick={saveProfile}
                  style={{ background: GOLD, color: 'black', border: 'none', padding: '14px 20px', borderRadius: '14px', fontWeight: '900', width: '100%', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif', fontSize: '0.78rem', letterSpacing: '1px', marginTop: '8px' }}
                  disabled={savingProfile}>
                  {savingProfile ? 'SAVING...' : 'SAVE PROFILE'}
                </button>
              </div>
            </div>
          </div>

        ) : (

          /* ── MAIN DASHBOARD ─────────────────────────────────────────────── */
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem' }}>
            <div className="user-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: '30px', marginTop: '1.5rem' }}>

              {/* LEFT */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '25px', minWidth: 0 }}>

                {/* Health Vector */}
                <div style={{ background: '#080c12', padding: '25px', borderRadius: '24px', border: '1px solid rgba(184,134,11,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <span style={{ color: GOLD, fontSize: '0.7rem', letterSpacing: '3px', fontWeight: 'bold' }}>HEALTH VECTOR ANALYSIS</span>
                    <span style={{ fontSize: '0.6rem', color: '#00ff88', fontWeight: 'bold' }}>LIVE MONITORING</span>
                  </div>
                  {healthAlerts.length > 0 ? healthAlerts.map((alert, idx) => (
                    <div key={idx} style={{ padding: '15px', background: 'rgba(184,134,11,0.05)', borderRadius: '15px', borderLeft: `4px solid ${GOLD}`, marginBottom: '10px' }}>
                      <p style={{ color: GOLD, fontSize: '0.8rem', fontWeight: 'bold', margin: '0 0 4px' }}>EARLY ACCESS: {alert.title || 'HEALTH ALERT'}</p>
                      <p style={{ color: '#888', fontSize: '0.65rem', margin: 0 }}>{alert.message}</p>
                    </div>
                  )) : (
                    <div style={{ padding: '15px', background: 'rgba(184,134,11,0.05)', borderRadius: '15px', borderLeft: `4px solid ${GOLD}` }}>
                      <p style={{ color: GOLD, fontSize: '0.8rem', fontWeight: 'bold', margin: '0 0 4px' }}>EARLY ACCESS: HAIR DENSITY STATUS</p>
                      <p style={{ color: '#888', fontSize: '0.65rem', margin: 0 }}>No critical density alerts detected.</p>
                    </div>
                  )}
                </div>

                {/* 3D Identity Card */}
                <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '24px', padding: '2rem', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <h2 style={{ color: GOLD, fontSize: '1.8rem', margin: 0 }}>3D Identity</h2>
                      {userData?.hasCompletedScan && (
                        <span style={{ background: GOLD, color: NAVY, fontSize: '0.5rem', fontWeight: '900', padding: '3px 8px', borderRadius: '4px' }}>SYNCED</span>
                      )}
                    </div>
                    <p style={{ opacity: 0.8, fontSize: '0.9rem', marginTop: '5px' }}>3D Cranial Solid calibration is active.</p>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                      {userData?.hasCompletedScan && (
                        <button onClick={() => navigate('/viewer')}
                          style={{ background: GOLD, color: NAVY, border: 'none', padding: '12px 24px', borderRadius: '50px', fontWeight: '900', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
                          View 3D
                        </button>
                      )}
                      <button
                        onClick={() => { if (!sub.canScan.allowed) setShowSubModal(true); else navigate('/scan?force=true'); }}
                        style={{ background: userData?.hasCompletedScan ? 'transparent' : GOLD, color: userData?.hasCompletedScan ? 'white' : NAVY, border: userData?.hasCompletedScan ? '1px solid white' : 'none', padding: '12px 24px', borderRadius: '50px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
                        {userData?.hasCompletedScan ? 'Redo 3D Scan' : 'Start New Scan'}
                      </button>
                    </div>
                  </div>
                  <div style={{ width: '110px', height: '110px', borderRadius: '50%', border: `2px solid ${GOLD}`, overflow: 'hidden', flexShrink: 0 }}>
                    {userData?.lastScanUrl
                      ? <video src={userData.lastScanUrl} muted autoPlay loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ height: '100%', display: 'grid', placeItems: 'center', background: '#111', color: GOLD, fontSize: '0.7rem', fontWeight: '900', letterSpacing: '2px' }}>ESDRAS</div>
                    }
                  </div>
                </div>

                {/* System Commands */}
                <div>
                  <p style={{ color: GOLD, fontSize: '0.7rem', letterSpacing: '3px', fontWeight: 'bold', marginBottom: '15px' }}>SYSTEM COMMANDS</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.2rem' }}>
                    {[
                      { title: '📸 Style Try-On', subtitle: 'Project hairstyles on your 3D head', primary: true, locked: !sub.canStyleTryOn.allowed && !sub.isSuperAdmin, onClick: () => { if (!sub.canStyleTryOn.allowed && !sub.isSuperAdmin) setShowSubModal(true); else setShowStyleTryOn(true); } },
                      { title: '📅 Appointments', subtitle: 'Manage bookings', primary: true, onClick: () => navigate('/bookings') },
                      { title: '🌍 Global Vault',  subtitle: 'Find trending styles', onClick: () => navigate('/gallery') },
                      { title: '💇 3D Viewer',     subtitle: 'My cranial solid',    onClick: () => navigate('/viewer') },
                      { title: '⚙️ Edit Profile',  subtitle: 'Update your info',    onClick: () => setShowProfile(true) },
                    ].map(card => (
                      <div key={card.title} onClick={card.onClick}
                        style={{ background: 'rgba(255,255,255,0.02)', padding: '1.5rem', borderRadius: '20px', cursor: 'pointer', border: `1px solid ${card.primary ? GOLD : 'rgba(255,255,255,0.1)'}`, opacity: card.locked ? 0.6 : 1 }}>
                        <h3 style={{ margin: 0, fontSize: '1rem', color: card.primary ? GOLD : 'white' }}>{card.title}</h3>
                        <p style={{ margin: '5px 0 0', fontSize: '0.7rem', opacity: 0.5 }}>{card.subtitle}</p>
                        {card.locked && <span style={{ fontSize: '0.55rem', color: '#ff4d4d', marginTop: '4px', display: 'block' }}>🔒 Subscribe to unlock</span>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Active Escrow */}
                {activeBookings.length > 0 && (
                  <div style={{ background: 'rgba(184,134,11,0.1)', borderRadius: '20px', padding: '1.5rem', border: `1px solid ${GOLD}` }}>
                    <p style={{ fontSize: '0.7rem', color: GOLD, fontWeight: 'bold', letterSpacing: '2px', marginBottom: '10px', textAlign: 'center' }}>ESCROW SECURITY ACTIVE</p>
                    {activeBookings.map(b => (
                      <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '12px', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                        <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.85rem' }}>{b.styleName} with {b.stylistName}</p>
                        <button onClick={() => releaseEscrow(b)}
                          style={{ background: GOLD, color: NAVY, border: 'none', padding: '8px 15px', borderRadius: '6px', fontWeight: 'bold', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
                          RELEASE FUNDS
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <Referral user={auth.currentUser} />
              </div>

              {/* RIGHT */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>
                {/* Wallet balance */}
                <div style={{ background: '#111', padding: '30px', borderRadius: '24px', textAlign: 'center', border: '1px solid #222' }}>
                  <p style={{ color: GOLD, fontSize: '0.6rem', letterSpacing: '2px', fontWeight: 'bold', margin: '0 0 10px' }}>VIRTUAL WALLET</p>
                  <h2 style={{ fontSize: '2rem', margin: '10px 0' }}>₦{userData?.walletBalance?.toLocaleString() || '0.00'}</h2>
                  <button onClick={() => navigate('/wallet')}
                    style={{ width: '100%', padding: '12px', background: GOLD, color: '#000', border: 'none', borderRadius: '12px', marginTop: '10px', fontWeight: 'bold', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
                    TOP UP CREDITS
                  </button>
                </div>

                {/* Stat boxes */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {[
                    { label: isSubActive ? `Scans · ${sub.tier?.toUpperCase()} (this month)` : 'Scans · Free Lifetime', value: `${scanUsed} / ${scanCap}`, color: scanUsed >= scanCap ? '#ff4d4d' : GOLD },
                    { label: isSubActive ? 'Style Try-ons · Unlimited' : 'Style Try-on · Free', value: isSubActive ? '∞' : `${tryOnUsed} / ${tryOnCap}`, color: (!isSubActive && tryOnUsed >= (sub.FREE_LIMITS?.styleTryOns || 1)) ? '#ff4d4d' : GOLD },
                    ...(userData?.densityScore ? [{ label: 'Density Score', value: `${(userData.densityScore * 100).toFixed(0)}%`, color: GOLD }] : []),
                  ].map(s => (
                    <div key={s.label} style={{ background: 'rgba(255,255,255,0.02)', padding: '1.2rem', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '0.6rem', opacity: 0.5 }}>{s.label.toUpperCase()}</p>
                      <p style={{ margin: '5px 0 0', fontSize: '1rem', fontWeight: 'bold', color: s.color }}>{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Upgrade CTA */}
                {!isSubActive && !sub.isSuperAdmin && (
                  <div style={{ background: 'rgba(184,134,11,0.08)', border: `1px solid rgba(184,134,11,0.25)`, borderRadius: '18px', padding: '18px', textAlign: 'center' }}>
                    <p style={{ color: GOLD, fontWeight: '700', fontSize: '0.78rem', margin: '0 0 6px' }}>🔓 Unlock More</p>
                    <p style={{ fontSize: '0.7rem', opacity: 0.7, margin: '0 0 12px' }}>Monthly: ₦3,000 · Yearly: ₦20,000</p>
                    <button onClick={() => setShowSubModal(true)}
                      style={{ width: '100%', padding: '12px', background: GOLD, color: '#000', border: 'none', borderRadius: '12px', fontWeight: 'bold', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
                      VIEW PLANS
                    </button>
                  </div>
                )}

                {/* Quick edit profile shortcut */}
                <button onClick={() => setShowProfile(true)}
                  style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', borderRadius: '14px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: '600', fontFamily: 'Montserrat, sans-serif' }}>
                  ⚙️ Edit Profile
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {showSubModal && (
        <SubscriptionPage paywallMode={true} paywallReason={!isSubActive ? 'free_exhausted' : null} onClose={() => setShowSubModal(false)} redirectAfter="/dashboard" />
      )}
      {showSupport    && <SupportTicket onClose={() => setShowSupport(false)} />}
      {showStyleTryOn && <StyleTryon onClose={() => setShowStyleTryOn(false)} />}
    </div>
  );
}

const superSwitch = { background: 'rgba(255,255,255,0.1)', color: GOLD, border: `1px solid ${GOLD}55`, padding: '10px 18px', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.65rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' };
const labelStyle  = { fontSize: '0.6rem', color: GOLD, fontWeight: 'bold', letterSpacing: '1px', display: 'block', marginBottom: '6px' };
const inputStyle  = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '13px 14px', borderRadius: '12px', width: '100%', fontFamily: 'Montserrat, sans-serif', fontSize: '0.82rem', transition: 'border-color 0.2s', boxSizing: 'border-box' };
